/**
 * drawOrderPlanForDrawing — cut the DRAWORDER strata plan from the DRAWING,
 * not from the entities that happened to survive the LOD pre-classify.
 *
 * # The gap this closes
 *
 * `drawOrderStrata.ts` explains what a stratum is and why the band fill needs
 * one. This module answers the other half: WHICH SEQUENCE the plan is cut in.
 *
 * On the cache-direct branch the feed `BatchedLayer.bulkLoad` receives is not
 * the drawing. `sceneBulkLoadHelpers.ts partitionStubsByLod` drops every stub
 * that measures sub-0.5 px at the fit-to-extents zoom, and `FrameLoops.tsx`
 * Pass 2 hands those ids to `BatchedLayer.addEntity` on zoom-in through the
 * `entityResolverRef` lift. A plan cut from the survivors has NO BOUNDARY
 * anywhere in the DRAWORDER band a dropped wide polyline occupies, so the band
 * relifts into stratum 0, its fill takes the stratum-0 floor `-0.5`, and every
 * line authored before it paints through its interior — the very defect the
 * strata exist to remove (@codex PR #785 round 13, P1).
 *
 * So the plan is cut over the FULL stub sequence: every entity the metadata
 * index holds for the spaces being rendered, in the DRAWORDER order
 * `state/spatialBuilder.ts buildSpatialRecordsForSpaces` assigned, lifted or
 * not. A never-lifted position contributes its bounds to the cover exactly
 * like a lifted one (it is authored, and it may be relifted at any zoom), and
 * may open a boundary of its own.
 *
 * # Where "is this a band?" comes from, and what it costs
 *
 * A metadata stub carries id / type / layer / colour / bounds and a degenerate
 * POINT geometry (`metadataToSpatialStub`) — never the widths. The answer is
 * therefore resolved per position, in the cheapest way that position allows:
 *
 *   1. LIFTED — the pass has already built the `RenderableEntity`, and its
 *      render-only SATELLITES are expanded into the sequence beside it (a
 *      block's children are authored positions of their own: a band child must
 *      open a boundary against an earlier SIBLING exactly as a top-level band
 *      does against an earlier entity, which is what the feed-cut plan did by
 *      walking `expandRenderSatellites`). The band-ness is read straight off
 *      the geometry the renderer is about to draw (`drawOrderStrata.ts
 *      drawsBandFill`). FREE: no resolve, no allocation.
 *   2. DROPPED, and of a type that can never draw a band (LINE, CIRCLE, ARC,
 *      TEXT, HATCH, …) — `false` from the type alone. FREE.
 *   3. DROPPED, polyline-kinded — one `resolveEntity` (a compact-store
 *      materialisation, the same unit the lift itself pays per liftable stub)
 *      and `drawing-model`'s OWN width rules ({@link typedDeclaresBandWidth}),
 *      so this module never re-derives the group 40/41/43 mode rule.
 *   4. DROPPED INSERT — one `resolveEntity` for the block NAME, then a MEMO
 *      per block definition: does that definition, or anything it nests,
 *      declare a width. Paid once per distinct block, not once per INSERT.
 *      Its children are not enumerated (that is the flatten this path exists
 *      to avoid), so the whole INSERT is ONE position: everything it draws
 *      lands in the stratum the INSERT opened. Ordering WITHIN such a block is
 *      therefore no finer than the INSERT — which is strictly better than what
 *      a relifted block child had before (the plan had never seen it at all,
 *      so it fell to stratum 0) and is recovered in full the moment the INSERT
 *      is lifted, by arm 1.
 *
 * The type gate in 2 is what keeps the added pass off the drawings that have
 * no wide polyline at all, and the geometric gate inside
 * `planDrawOrderStrataFrom` (a position is asked at all only when its bounds
 * already intersect the current stratum's cover) keeps it off the rest.
 *
 * # Conservative in ONE direction only
 *
 * Every "could not decide" answer here is `true` — an EXTRA boundary, which
 * costs draw calls and paints exactly the same pixels. The opposite mistake, a
 * band that opens no boundary, is the bug. That is why an INSERT whose block
 * definition is missing, whose nesting is cyclic, or whose content is an
 * MLEADER / DIMENSION (whose own block content this module does not flatten)
 * counts as a band: those kinds are exempt from the sub-pixel drop
 * (`lod/lodPolicy.ts isAlwaysLiftedStubType`), so in practice they are always
 * resolved by arm 1 and the conservative arm is unreachable — but if the
 * exemption ever changes, the plan gets coarser, never wrong.
 */

import {
  hasPolylineWidth,
  heavyPolylineDeclaresWidth,
  lwpolylineWidths,
  type BlockDefinition,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { fillModePaintsSolid } from "../adapters/geom/polylineDrawnBand";
import {
  drawsBandFill,
  planDrawOrderStrataFrom,
  type DrawOrderStrata,
} from "../drawOrderStrata";
import { isMetadataStubRenderable } from "../state/metadataStub";
import type { RenderableEntity } from "../types";

import { collectRenderSatellites } from "./renderSatellites";

/**
 * Entity types that can put a SOLID BAND on the canvas. Everything else is
 * answered `false` from the stub's type, with no resolve at all.
 *
 * `LWPOLYLINE` / `Polyline` draw one themselves; `Block` draws one through a
 * flattened child; `Multileader` / `Dimension` through block content this
 * module does not walk (see the module doc's conservative note).
 */
const BAND_CAPABLE_STUB_TYPES: ReadonlySet<string> = new Set([
  "LWPOLYLINE",
  "Polyline",
  "Block",
  "Multileader",
  "Dimension",
]);

/**
 * Does this typed entity declare a width of its own?
 *
 * Both arms are `drawing-model`'s OWN rules, composed rather than re-derived:
 * the heavy POLYLINE has a named entity-level predicate
 * ({@link heavyPolylineDeclaresWidth}), and the LWPOLYLINE is the same shape
 * written out — the ONE width channel the entity is in
 * ({@link lwpolylineWidths}, which decides VARIABLE-vs-CONSTANT mode) fed to
 * the ONE "does this count as width" test ({@link hasPolylineWidth}). Nothing
 * about group 40 / 41 / 43 is decided here. When `drawing-model` grows the
 * named LWPOLYLINE counterpart, this arm becomes a call to it.
 */
function typedDeclaresBandWidth(typed: DrawingEntity): boolean {
  if (typed.type === "LWPOLYLINE") {
    return hasPolylineWidth(lwpolylineWidths(typed));
  }
  if (typed.type === "Polyline") return heavyPolylineDeclaresWidth(typed);
  return false;
}

/**
 * Does `name`'s definition — or anything it nests — declare a width?
 * Memoised across the whole plan, so a drawing with 40 000 INSERTs of 30
 * distinct blocks walks 30 definitions.
 *
 * `inProgress` is the recursion PATH, so a cyclic definition is answered
 * `true` (conservative) for the arm that closes the cycle rather than looping.
 * An unknown name is `true` for the same reason.
 */
function blockDefinitionDeclaresBandWidth(
  name: string,
  blocks: Record<string, BlockDefinition>,
  memo: Map<string, boolean>,
  inProgress: Set<string>,
): boolean {
  const cached = memo.get(name);
  if (cached !== undefined) return cached;
  const definition = blocks[name];
  if (!definition) return true;
  if (inProgress.has(name)) return true;
  inProgress.add(name);
  let declares = false;
  for (const raw of definition.entities ?? []) {
    const child = raw as DrawingEntity & { blockName?: string };
    if (typedDeclaresBandWidth(child)) {
      declares = true;
      break;
    }
    if (child.type === "Block") {
      const childName = child.blockName;
      if (
        childName === undefined ||
        blockDefinitionDeclaresBandWidth(childName, blocks, memo, inProgress)
      ) {
        declares = true;
        break;
      }
      continue;
    }
    if (child.type === "Multileader" || child.type === "Dimension") {
      // Block content this module does not flatten — see the module doc.
      declares = true;
      break;
    }
  }
  inProgress.delete(name);
  memo.set(name, declares);
  return declares;
}

/**
 * Cut the DRAWORDER strata plan for a cache-direct pass.
 *
 * @param stubs the WHOLE drawing in DRAWORDER — the array
 *   `buildSpatialRecordsForSpaces` produced for the rendered spaces, after the
 *   annotative filter and the bounds reprojection, BEFORE the LOD drop.
 * @param lifted the top-level renderables this pass actually built
 *   (un-expanded: their satellites are reached through
 *   `collectRenderSatellites`).
 * @param resolveEntity the pass's typed resolver (`SceneBulkLoadCallbacks`).
 * @param blocks the drawing's block table.
 * @param fillmode the drawing's `$FILLMODE`; `0` means no band anywhere is
 *   filled, so no band can cover anything and no boundary is ever cut.
 */
export function planDrawOrderStrataForDrawing(args: {
  stubs: readonly RenderableEntity[];
  lifted: readonly RenderableEntity[];
  resolveEntity: (id: string) => DrawingEntity | null;
  blocks: Record<string, BlockDefinition>;
  fillmode: number | undefined;
}): DrawOrderStrata {
  const { stubs, lifted, resolveEntity, blocks, fillmode } = args;
  const liftedById = new Map<string, RenderableEntity>();
  for (const r of lifted) liftedById.set(r.id, r);
  const paintsSolid = fillModePaintsSolid(fillmode);
  const blockMemo = new Map<string, boolean>();
  const inProgress = new Set<string>();

  // The DRAWORDER sequence: every stub in the drawing's order, with a lifted
  // position replaced by the renderables it actually draws — itself and its
  // satellites, in the order `expandRenderSatellites` feeds the batcher.
  const sequence: RenderableEntity[] = [];
  for (const stub of stubs) {
    const known = liftedById.get(stub.id);
    if (known === undefined) {
      sequence.push(stub);
      continue;
    }
    sequence.push(known);
    for (const child of collectRenderSatellites(known)) sequence.push(child);
  }

  const opensBandStratum = (item: RenderableEntity): boolean => {
    // A LIFTED position (or one of its satellites) carries the geometry the
    // renderer will draw; the answer is read off it, exactly as the feed-cut
    // plan reads it. `isMetadataStubRenderable` is the one truth for telling
    // the two apart (`state/metadataStub.ts`).
    if (!isMetadataStubRenderable(item)) return drawsBandFill(item);
    if (!paintsSolid) return false;
    if (!BAND_CAPABLE_STUB_TYPES.has(item.type)) return false;
    const typed = resolveEntity(item.id);
    if (!typed) {
      // The id is in the index but the document cannot produce it. It is not
      // drawable at all, so it cannot paint a band either.
      return false;
    }
    if (typed.type === "Block") {
      const name = (typed as DrawingEntity & { blockName?: string }).blockName;
      return (
        name === undefined ||
        blockDefinitionDeclaresBandWidth(name, blocks, blockMemo, inProgress)
      );
    }
    if (typed.type === "Multileader" || typed.type === "Dimension") return true;
    return typedDeclaresBandWidth(typed);
  };

  return planDrawOrderStrataFrom(sequence, opensBandStratum, "stratum-zero");
}
